import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { computed, ref } from 'vue';
import PillLabel from '@/components/widgets/filters/PillLabel.vue';
import PillVariation from '@/components/widgets/filters/PillVariation.vue';
import PillCountLabel from '@/components/widgets/filters/PillCountLabel.vue';
import FilterIcon from '@/components/widgets/filters/FilterIcon.vue';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown.vue';
import useTranslate from '../../utilities/composables/useTranslate';

interface Variation {
  id: string;
  label: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchPill',
  props: {
    label: {},
    pluralLabel: { default: '' },
    icon: {},
    preselectedVariation: { default: '' },
    dropdownOptions: { default: () => [] },
    variations: { default: () => [] },
    showSearchInput: { type: Boolean, default: true },
    dropdownPlaceholder: { default: 'Search' },
    emptyMeansAll: { type: Boolean, default: false },
    singleValue: { type: Boolean, default: false },
    selectedValue: { type: Boolean, default: false }
  },
  emits: ['value-change', 'variation-change', 'dropdown-closed'],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

const { translate } = useTranslate();

const searchQuery = ref('');

const filteredDropdownOptions = computed(() => {
  return props.dropdownOptions.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});
const filterPill = ref<any>(null);
const filterDropdown = ref<any>(null);
const filterPillLabel = ref(null);

const variationsEnabled = computed(() => {
  if (!props.variations.length) {
    return [];
  }
  // exclude should be disabled when everything is selected
  return (props.variations as Variation[])?.map?.((v: Variation) => {
    return {
      ...v,
      disabled: everythingSelected.value && v.id.includes('exclude'),
    };
  });
});

const selectedValuesCount = computed(() => {
  if (props.singleValue) {
    return props.selectedValue ? 1 : 0;
  }
  return props.dropdownOptions.filter((option) => option.checked).length;
});

const pillLabel = computed(() => {
  if (selectedValuesCount.value > 0 && !props.singleValue) {
    return `${props.label}:`;
  }
  return props.label;
});

const singleSelectedValue = computed(() => {
  if (selectedValuesCount.value !== 1) {
    return null;
  }
  return props.dropdownOptions.find((option) => option.checked);
});

const everythingSelected = computed(() => {
  return (
    selectedValuesCount.value === props.dropdownOptions.length ||
    (selectedValuesCount.value === 0 && props.emptyMeansAll)
  );
});

const firstThreeSelectedValues = computed(() => {
  return props.dropdownOptions.filter((option) => option.checked).slice(0, 3);
});

const countLabel = computed(() => {
  if (selectedValuesCount.value === 0 && !props.emptyMeansAll) {
    return translate('none');
  }
  if (selectedValuesCount.value === 1) {
    return singleSelectedValue.value?.label;
  }
  if (everythingSelected.value) {
    return translate('all');
  }
  return `${selectedValuesCount.value} ${props.pluralLabel.toLowerCase()}`;
});

const emitValueChange = (value: any) => {
  emit('value-change', value);
};

const itemClicked = (item: any) => {
  emitValueChange(item);
};

const setVariation = (variation: string) => {
  emit('variation-change', variation);
};

const hideFilterPillInstance = () => {
  filterPill.value?.hide?.();
};

const itemRightSideClick = (item: any) => {
  hideFilterPillInstance();
  emitValueChange(item);
  emit('dropdown-closed');
};

const checkItem = (item: any) => {
  emitValueChange(item);
};

const onVariationDropdownShown = () => {
  hideFilterPillInstance();
};

const escHandler = () => {
  hideFilterPillInstance();
};

const dropdownQueryChange = ({ value }: { value: string }) => {
  searchQuery.value = value;
};

const onShow = () => {
  if (props.singleValue) return false;
  filterDropdown.value?.focusInput?.();
};

const onHide = () => {
  searchQuery.value = '';
  emit('dropdown-closed');
};

const pillClick = () => {
  if (props.singleValue) {
    emitValueChange(!props.selectedValue);
    emit('dropdown-closed');
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_popover = _resolveComponent("v-popover")!

  return (_openBlock(), _createBlock(_component_v_popover, {
    ref_key: "filterPill",
    ref: filterPill,
    interactive: "",
    tag: "div",
    "content-tag": "div",
    trigger: "click",
    placement: "bottom-start",
    role: 'poper',
    "on-show": onShow,
    "on-hide": onHide
  }, {
    content: _withCtx(() => [
      _createVNode(FilterDropdown, {
        ref_key: "filterDropdown",
        ref: filterDropdown,
        placeholder: _ctx.dropdownPlaceholder,
        "hide-filter-popup": escHandler,
        "search-results": filteredDropdownOptions.value,
        "on-item-click": itemClicked,
        "on-item-right-side-click": itemRightSideClick,
        "select-only-search-result": checkItem,
        "show-search-input": _ctx.showSearchInput,
        onQueryChange: dropdownQueryChange,
        onEsc: escHandler
      }, null, 8, ["placeholder", "search-results", "show-search-input"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: _normalizeClass(["flex border border-solid rounded-2xl pl-1 min-w-0 pt", {
        'pr-1': !_ctx.dropdownOptions.length,
        'border-gray-500': !selectedValuesCount.value,
        'bg-highlight-bg': selectedValuesCount.value > 0,
        'border-blue-500': selectedValuesCount.value > 0,
      }]),
        onClick: pillClick
      }, [
        (!selectedValuesCount.value || _ctx.singleValue)
          ? (_openBlock(), _createBlock(PillLabel, {
              key: 0,
              ref_key: "filterPillLabel",
              ref: filterPillLabel,
              "icon-name": _ctx.icon,
              "icon-class": 'w-3 h-3',
              class: _normalizeClass(['shrink-0', { 'pr-0': !variationsEnabled.value.length }, { 'pr-1': _ctx.singleValue }]),
              label: pillLabel.value
            }, null, 8, ["icon-name", "class", "label"]))
          : _createCommentVNode("", true),
        _cache[2] || (_cache[2] = _createTextVNode()),
        (_ctx.variations.length)
          ? (_openBlock(), _createBlock(PillVariation, {
              key: 1,
              variations: variationsEnabled.value,
              "preselected-variation": _ctx.preselectedVariation,
              class: "shrink-0 min-w-0",
              onSetVariation: setVariation,
              onVariationDropdownOpened: onVariationDropdownShown
            }, null, 8, ["variations", "preselected-variation"]))
          : _createCommentVNode("", true),
        _cache[3] || (_cache[3] = _createTextVNode()),
        (!_ctx.singleValue)
          ? (_openBlock(), _createElementBlock("div", {
              key: 2,
              ref_key: "filterDropdown",
              ref: filterDropdown,
              class: "flex py-0 px-1 min-w-0"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(firstThreeSelectedValues.value, (item, index) => {
                return (_openBlock(), _createBlock(FilterIcon, {
                  key: item.id,
                  "icon-name": item?.customIcon,
                  "icon-type": item?.iconType,
                  color: item?.customIconColor,
                  class: _normalizeClass([{
            'mr-1': index === firstThreeSelectedValues.value.length - 1,
          }, "flex items-center justify-center px-0 mr"]),
                  "icon-class": 'text-gray-500'
                }, null, 8, ["icon-name", "icon-type", "color", "class"]))
              }), 128)),
              _cache[0] || (_cache[0] = _createTextVNode()),
              (selectedValuesCount.value > 0)
                ? (_openBlock(), _createBlock(PillCountLabel, {
                    key: 0,
                    "count-label": countLabel.value,
                    "values-count": selectedValuesCount.value
                  }, null, 8, ["count-label", "values-count"]))
                : _createCommentVNode("", true),
              _cache[1] || (_cache[1] = _createTextVNode()),
              _createVNode(FilterIcon, {
                "icon-name": 'chevron-down',
                class: _normalizeClass([{
            'pl-0': selectedValuesCount.value === 0,
          }, "flex items-center justify-center px-1"]),
                "icon-class": 'bg-icon-default'
              }, null, 8, ["class"])
            ], 512))
          : _createCommentVNode("", true)
      ], 2),
      _cache[4] || (_cache[4] = _createTextVNode())
    ]),
    _: 1
  }, 512))
}
}

})